import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import type { trpc } from "@/lib/trpc";

export interface RouterContext {
  queryClient: QueryClient;
  trpc: typeof trpc;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/**
 * A Vercel 404: the code in mono above one plain heading, a line of help and
 * the way back, centred on the page colour with nothing else around it.
 */
function NotFound() {
  return (
    <main className="flex min-h-svh flex-col items-center justify-center bg-background-200 px-6 pt-[env(safe-area-inset-top)] pb-[max(10svh,env(safe-area-inset-bottom))] text-center">
      <p className="mb-4 text-gray-900 text-label-14-mono tabular-nums">404</p>
      <h1 className="mb-3 text-balance text-gray-1000 text-heading-24 md:text-heading-32">
        This page could not be found.
      </h1>
      <p className="mb-8 max-w-md text-balance text-copy-16 text-gray-900">
        The link may be broken, or the page may have moved.
      </p>
      <Button asChild className="h-10 pointer-coarse:h-10">
        <Link to="/devices">Back to Devices</Link>
      </Button>
    </main>
  );
}

function RootComponent() {
  return (
    // `system` is the default so a first visit follows the browser, and keeps
    // following it live. The storage key is shared with the pre-paint script in
    // index.html, and — because localStorage is per origin — it is also what
    // syncs the popout window to the tab that opened it.
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      storageKey="theme"
    >
      <Outlet />
      <Toaster richColors position="bottom-right" />
    </ThemeProvider>
  );
}
